const { version } = require('../package.json');
import state from '@/state';
// Handles openPopupProfile event
import '@/components/PopupProfileCard/popupProfileCardEventHook';
import '@/widgets/SocialFeed/openSocialModalEventHook';
import '@/widgets/Tutorial/openTutorialModalEventHook';
import openAvatarConfigurator from '@/components/AvatarConfigurator/openAvatarConfigurator';

import { defineAsyncComponent } from 'vue';

const NotificationsToasts = defineAsyncComponent(() => {
  return import(/* webpackChunkName: "stage-2" */'@/widgets/Notifications/Toasts');
});
import { createAvatarConfigurator } from '@/legacy/avatar-configurator';
import getMerchantConfig from '@/utils/getMerchantConfig';
import createEl from '@/utils/createEl';
import openSocialModal from '@/widgets/SocialFeed/openSocialModal';
import { createApp } from 'vue';
import { alias, init as initElAttach } from '@/utils/elAttach';
import eventbus from '@/utils/eventbus';
import { getMyProfile } from '@/utils/getMyProfile';
import hooks from '@/utils/hooks';
import openPopupProfileCard from './components/PopupProfileCard/openPopupProfileCard';
import createLootCratesModal from '@/widgets/LootCrate/createLootCratesModal';
import '@/utils/magicVariableManager';

import openTutorialModal from '@/widgets/Tutorial/openTutorialModal';

import callApi from '@/utils/callApi';
import { onNavigation, toastStatus, createStickyToast, STICKY_DELAY_MS, TOAST_STATUS } from './utils/stickyToastManager';

import '@/components/Debug/debugHandler.js';

// Sticky toasts initialization
if (state.merchantConfig.widgets.stickyToast?.enabled && toastStatus != TOAST_STATUS.CLOSED) {
  setTimeout(async () => {
    const shouldTranslate = state.merchantConfig?.widgets?.translations.mode !== 'translateNothing';
    const res = await callApi('GET', state.commentSearchEndpoint + `?take=1&pin=sticky_toast&translate=${shouldTranslate}`);
    const data = await res.json();
    if (data.comments.length) {
      onNavigation(() => {
        createStickyToast({ type: 'pinned-post', comment: data.comments[0] });
      });
    }
  }, STICKY_DELAY_MS);
}

let cssTag;


import openConversation from '@/components/DirectMessages/openConversation';
window.openConversation = openConversation;

import { runQueryActions } from './queryActions';

eventbus.on('profileLoaded', (profile) => {
  if (profile) {
    const tutorialEnabled = state.merchantConfig?.widgets?.tutorial?.enabled;
    const tutorialAutoOpenEnabled = state.merchantConfig?.widgets?.tutorial?.autoOpen;
    const hasCompletedTutorial = (profile?.flags || []).includes('completed_tutorial');
  
    if (tutorialEnabled && tutorialAutoOpenEnabled && ! hasCompletedTutorial) {
      openTutorialModal();
    }
  }
});

function onProfileLoaded () {
  if (state.profile) {
    // get from local session storage
    const ts = window.sessionStorage.getItem('gmf_avatar_timestamp');
    if (ts) {
      state.avatarTimestamps[state.profile.id] = parseInt(ts);

      // If the avatar timestamp is older than 1 hour, update it
      if (Date.now() - state.avatarTimestamps[state.profile.id] > 60 * 60 * 1000) {
        state.avatarTimestamps[state.profile?.id] = Date.now();
        window.sessionStorage.setItem('gmf_avatar_timestamp', state.avatarTimestamps[state?.profile?.id]);
      }
    } else {
      state.avatarTimestamps[state.profile?.id] = Date.now();
      window.sessionStorage.setItem('gmf_avatar_timestamp', state.avatarTimestamps[state?.profile?.id]);
    }
  }
}

async function init() {
  if (process.env.NODE_ENV === 'development') {
    console.log('Gamifiera SDK v' + version + ' %c[DEV BUILD]', 'background: green; color: white;', window.gmf_config);
  } else {
    console.log('Gamifiera SDK v' + version);
  }

  let mc = await getMerchantConfig();

  if (mc?.merchant?.disabled) {
    throw new Error('Your Gamifiera account is suspended. Contact support for more information.');
  }

  // Replace gmf function handler and handle calls made before this script was loaded
  if (window._gmf) {
    for (const args of window._gmf) {
      runFn(...args);
    }
  }
  delete window._gmf;

  window.gmf = runFn;

  state.merchantConfig = mc || {};

  if (!state.customerToken) {
    state.profile = null;
  } else {
    const profile = await getMyProfile();
    state.profile = profile || null;
    onProfileLoaded();

    eventbus.emit('profileLoaded', state.profile);
  }

  loadMerchantConfig(mc);

  /*

  */

  if (mc.widgets.toasts && mc.widgets.toasts.enabled) {
    const el = window.document.createElement('div');
    window.document.body.appendChild(el);

    const app = createApp(NotificationsToasts)
    .mount(el);

    // @TODO is this needed here?
    if (app.config) app.config.unwrapInjectedRef = true;
  }

  runQueryActions();

  if (window.gmf_config.onLoad) {
    window.gmf_config.onLoad();
  }
}
init();

import '@/widgets';


async function runFn (action, params, ...moreParams) {
  if (action === 'init') {
    console.error('GMF init was called again after load, this is not supported and will not have any effect. Use update instead to change values');
  } else if (action == 'update') {
    if (process.env.NODE_ENV === 'development') {
      console.log('GMF update', params);
    }
    
    if ('customerToken' in params) {
      await setCustomerToken(params.customerToken);
    }

    if ('locale' in params) {
      // @TODO support both sv_SE and sv-SE
      state.setLocale(params.locale || 'sv_SE');
    } else if ('lang' in params) {
      state.setLocaleViaLang(params.lang);
    } else if (action === 'init') {
      state.setLocale('sv_SE');
    }
  } else if (action === 'on') {
    hooks.on(params, moreParams[0]);
  } else if (action === 'off') {
    hooks.off(params, moreParams[0]);
  } else if (action === 'showAvatarConfigurator') {
    // 2d avatar config
    if (typeof params === 'object') {
      createAvatarConfigurator(params);
    } else {
      createAvatarConfigurator({ selector: params });
    }
  } else if (action === 'openAvatarConfigurator') {
    openAvatarConfigurator();
  } else if (action === 'showLootCrateOpenModal') {
    // @TODO deprecation warning
    if (typeof params === 'object') {
      createLootCratesModal(params);
    } else {
      console.error('Invalid parameter type for action "showLootCrateOpenModal"');
    }
  } else if (action === 'showCommentSection') {
    console.error('showCommentSection is deprecated, use the gmf-comment-section widget instead');
  } else if (action === 'openSocialModal') {
    openSocialModal(Object.assign({
      // scrollToCommentId: comment ? comment.id : null,
      displayHeader: true,
    }, params));
  } else if (action === 'openDropdownProfile') { 
    console.log('openDropdownProfile is deprecated, use openCustomerCard instead');
  } else if (action === 'openCustomerCard') {
    /*
    Usage:
    gmf('openCustomerCard', {
      customerId: '123',
      tab: 'achievements',
    });
    */

    if (params && typeof params !== 'object') {
      console.error('Invalid parameter type for action "openCustomerCard", expected object');
      return;
    }
    openPopupProfileCard(params?.customerId, params?.tab)
  } else if (action === 'createLootCratesModal') {
    if (typeof params === 'object') {
      createLootCratesModal(params)
    } else {
      console.error('Invalid parameter type for action "createLootCratesModal"');
    }
  } else if (action === 'openTutorialModal') {
    if (typeof params === 'object') {
      openTutorialModal(params)
    } else {
      console.error('Invalid parameter type for action "openTutorialModal"');
    }
  } else if (action === 'replaceDebugConfig') {
    state.merchantConfig = params;
    loadMerchantConfig(state.merchantConfig);
  } else if (action === 'reloadIcons') {
    eventbus.emit('reloadIcons');
  } else if (action === 'setCustomerToken') {
    setCustomerToken(params);
  } else {
    console.error('Unknown action', action);
  }
}

function loadMerchantConfig (mc) {
  // Build style
  let css = '';

  /*
  * CSS hacks
  */
  if (mc.css && mc.css.length > 0) {
    css += mc.css;
  }

  // Apply css vars
  if (mc && mc.widgets && mc.widgets.cssvars) {
    css += 'body {\n';
    for (const key of (Object.keys(mc.widgets.cssvars))) {
      css += '  --' + key + ': ' + mc.widgets.cssvars[key] + ';\n';
    }
    css += '}\n';
  }

  /*
  * Icons
  */
  const icons = mc.widgets && mc.widgets.icons || {};

  icons['notification-bell'] = 'https://cdn.gamifiera.com/static/magic-icons/bell.svg';
  icons['notification-bell-hover'] = 'https://cdn.gamifiera.com/static/magic-icons/bell.svg';
  if (mc.notifications && mc.notifications['dropdown-icon']) {
    icons['notification-bell'] = mc.notifications['dropdown-icon'];
  }
  if (mc.notifications && mc.notifications['dropdown-icon-hover']) {
    icons['notification-bell-hover'] = mc.notifications['dropdown-icon-hover'];
  }

  css += 'body {\n';
  for (const key of (Object.keys(icons))) {
    const value = icons[key];
    if (value) {
      css += '  --gmf-icon-' + key + ': url(' + value + ');\n';
    }
  }
  css += '}\n';

  const variant = (window.gmf_config?.variant || 'a').toLowerCase();
  // a variant is without additional changes
  if (variant === 'b' && mc.css_b) {
    css += '\n' + mc.css_b + '\n';
    console.log('Using B variant');
  } else if (variant === 'c' && mc.css_c) {
    css += '\n' + mc.css_c + '\n';
    console.log('Using C variant');
  } else if (! (['a', 'b', 'c']).includes(variant)) {
    console.error('Invalid variant', variant);
  }

  /*
  * Apply config css
  */
  if (! cssTag) {
    cssTag = createEl('style', null, document.body);
  }
  cssTag.textContent = css;

  if (mc.i18nOverrides) {
    state.applyi18nOverrides(mc.i18nOverrides);
  }

  if (mc.widgets && mc.widgets.aliases) {
    for (const key in mc.widgets.aliases) {
      alias(key, mc.widgets.aliases[key]);
    }
  }

  initElAttach();
}

async function setCustomerToken (customerToken) {
  state.customerToken = customerToken || null;
  if (customerToken === 'null') {
    state.customerToken = null;
    console.error('Customer token was set to string value "null", use actual null value instead');
  } else if (typeof state.customerToken === 'string') {
    // When the customer token is stored as a cookie, the + character turns to spaces. Lets fix
    state.customerToken = state.customerToken.replace(/\s/g, '+');
  }

  eventbus.emit('update:customerToken');
  
  if (!state.customerToken) {
    state.profile = null;
  } else {
    const profile = await getMyProfile();
    state.profile = profile || null;
    onProfileLoaded();

    eventbus.emit('profileLoaded', state.profile);
  }
}

import './style.css';
